/**
 * Error Handling Utilities (Frontend)
 *
 * Type-safe error extraction and handling utilities for the Parse bridge frontend.
 * Replaces unsafe `catch (error: any)` patterns with proper error handling.
 *
 * @see docs/architecture/error-handling-design.md for design details
 */

/**
 * Error types for categorizing wallet/bridge errors
 */
export type ErrorType =
  // Transfer flow errors
  | 'BurnError'           // Failed to burn tokens
  | 'AttestationError'    // Failed to get/process attestation
  | 'MintError'           // Failed to mint tokens

  // Wallet errors
  | 'WalletConnectionError'
  | 'WalletRejectionError'
  | 'InsufficientFundsError'
  | 'ChainSwitchError'
  | 'TransactionError'

  // API errors
  | 'ApiError'
  | 'NetworkError'
  | 'TimeoutError'

  // General
  | 'UnknownError';

/**
 * Extracted error with all relevant fields
 */
export interface ExtractedError {
  message: string;
  code?: string | number;
  stack?: string;
  name?: string;
}

/**
 * Safely extract error message from any thrown value
 *
 * Handles common wallet/web3 error shapes:
 * - Standard Error objects
 * - Wallet errors (MetaMask, WalletConnect, etc.)
 * - Solana wallet adapter errors
 * - API response errors
 *
 * @example
 * ```ts
 * catch (error) {
 *   const message = getErrorMessage(error);
 *   setError(message);
 * }
 * ```
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }

  if (typeof error === 'string') {
    return error;
  }

  if (typeof error === 'object' && error !== null) {
    // Handle objects with message property
    if ('message' in error && typeof (error as { message: unknown }).message === 'string') {
      return (error as { message: string }).message;
    }

    // Handle Solana wallet adapter errors
    if ('error' in error && typeof (error as { error: unknown }).error === 'object') {
      const innerError = (error as { error: { message?: string } }).error;
      if (innerError?.message) {
        return innerError.message;
      }
    }

    // Handle objects with error string property
    if ('error' in error && typeof (error as { error: unknown }).error === 'string') {
      return (error as { error: string }).error;
    }

    // Handle API response format { success: false, error: { message: "..." } }
    if ('error' in error) {
      const errObj = (error as { error: unknown }).error;
      if (typeof errObj === 'object' && errObj !== null && 'message' in errObj) {
        return (errObj as { message: string }).message;
      }
    }

    // Handle reason property (ethers.js errors)
    if ('reason' in error && typeof (error as { reason: unknown }).reason === 'string') {
      return (error as { reason: string }).reason;
    }

    // Try to stringify
    try {
      const str = JSON.stringify(error);
      if (str !== '{}') return str;
    } catch {
      // Ignore stringify errors
    }
  }

  return 'An unknown error occurred';
}

/**
 * Safely extract error code from thrown value
 *
 * Handles common patterns:
 * - EIP-1193 error codes (4001 = user rejected, etc.)
 * - HTTP status codes
 * - Solana error codes
 *
 * @example
 * ```ts
 * catch (error) {
 *   const code = getErrorCode(error);
 *   if (code === 4001) {
 *     // User rejected - don't show error toast
 *     return;
 *   }
 * }
 * ```
 */
export function getErrorCode(error: unknown): string | number | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  // Check for direct code property
  if ('code' in error) {
    const code = (error as { code: unknown }).code;
    if (typeof code === 'string' || typeof code === 'number') {
      return code;
    }
  }

  // Check for nested error.code (Solana wallet adapter, ethers v6 "could not coalesce" wrapper —
  // which carries the real provider error, e.g. { code: 5000 }, under `.error`).
  if ('error' in error) {
    const innerError = (error as { error: unknown }).error;
    if (typeof innerError === 'object' && innerError !== null && 'code' in innerError) {
      const code = (innerError as { code: unknown }).code;
      if (typeof code === 'string' || typeof code === 'number') {
        return code;
      }
    }
  }

  // Check for ethers v6 doubly-nested provider error (error.info.error.code) — JsonRpcProvider
  // surfaces the wallet's EIP-1193 error here when it cannot coalesce it to a known ethers code.
  if ('info' in error) {
    const info = (error as { info: unknown }).info;
    if (typeof info === 'object' && info !== null && 'error' in info) {
      const infoErr = (info as { error: unknown }).error;
      if (typeof infoErr === 'object' && infoErr !== null && 'code' in infoErr) {
        const code = (infoErr as { code: unknown }).code;
        if (typeof code === 'string' || typeof code === 'number') {
          return code;
        }
      }
    }
  }

  // Check for HTTP status code
  if ('status' in error && typeof (error as { status: unknown }).status === 'number') {
    return (error as { status: number }).status;
  }

  // Check for response.status (fetch errors)
  if ('response' in error) {
    const response = (error as { response: unknown }).response;
    if (typeof response === 'object' && response !== null && 'status' in response) {
      const status = (response as { status: unknown }).status;
      if (typeof status === 'number') {
        return status;
      }
    }
  }

  return undefined;
}

/**
 * Safely extract stack trace from thrown value
 * Only included in development mode
 */
export function getErrorStack(error: unknown): string | undefined {
  if (process.env.NODE_ENV !== 'development') {
    return undefined;
  }

  if (error instanceof Error && error.stack) {
    return error.stack;
  }
  return undefined;
}

/**
 * Safely extract error name from thrown value
 */
export function getErrorName(error: unknown): string | undefined {
  if (error instanceof Error) {
    return error.name;
  }
  if (typeof error === 'object' && error !== null && 'name' in error) {
    const name = (error as { name: unknown }).name;
    if (typeof name === 'string') {
      return name;
    }
  }
  return undefined;
}

/**
 * Extract Solana SendTransactionError logs (where "insufficient lamports N, need M" etc. actually live —
 * the `.message` is often just "Transaction simulation failed: custom program error: 0x1"). Joined into a
 * single string for text matching by the classifier. Empty string when there are no logs.
 */
export function getErrorLogs(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'logs' in error) {
    const logs = (error as { logs: unknown }).logs;
    if (Array.isArray(logs)) {
      return logs.filter((l): l is string => typeof l === 'string').join(' ');
    }
  }
  return '';
}

/**
 * Extract all error information in a single call
 *
 * @example
 * ```ts
 * catch (error) {
 *   const { message, code } = extractError(error);
 *   if (code === 4001) {
 *     console.log('User cancelled');
 *   } else {
 *     setError(message);
 *   }
 * }
 * ```
 */
export function extractError(error: unknown): ExtractedError {
  return {
    message: getErrorMessage(error),
    code: getErrorCode(error),
    stack: getErrorStack(error),
    name: getErrorName(error),
  };
}

/**
 * Check if error is a user rejection (wallet prompt declined)
 *
 * Common rejection codes:
 * - 4001: EIP-1193 user rejected request
 * - ACTION_REJECTED: ethers.js user rejection
 * - User rejected: common message pattern
 *
 * @example
 * ```ts
 * catch (error) {
 *   if (isUserRejection(error)) {
 *     // User intentionally cancelled - not an error
 *     setStatus('idle');
 *     return;
 *   }
 *   setError(getErrorMessage(error));
 * }
 * ```
 */
export function isUserRejection(error: unknown): boolean {
  const code = getErrorCode(error);
  const message = getErrorMessage(error).toLowerCase();

  // EIP-1193 user rejection code
  if (code === 4001) return true;

  // ethers.js rejection code
  if (code === 'ACTION_REJECTED') return true;

  // Solana wallet adapter rejection
  if (code === 'WalletSignTransactionError') {
    if (message.includes('rejected') || message.includes('cancelled')) {
      return true;
    }
  }

  // Message patterns
  if (message.includes('user rejected')) return true;
  if (message.includes('user denied')) return true;
  if (message.includes('user cancelled') || message.includes('user canceled')) return true;
  if (message.includes('rejected the request')) return true;
  if (message.includes('transaction was rejected')) return true;

  // TronLink rejection
  if (message.includes('confirmation declined')) return true;

  // Wallet-declined (EIP-1193 code 5000, e.g. Phantom's "Transaction declined"). A declined tx is
  // never broadcast → no funds move, so treat it as a benign cancellation like 4001. Require the
  // decline/reject context alongside the code so an unrelated 5000 isn't swallowed as a rejection.
  if (code === 5000 && (message.includes('declin') || message.includes('reject') || message.includes('cancel'))) {
    return true;
  }
  if (message.includes('transaction declined')) return true;
  if (message.includes('declined the transaction')) return true;

  return false;
}

/**
 * Check if error indicates insufficient funds
 */
export function isInsufficientFundsError(error: unknown): boolean {
  const code = getErrorCode(error);
  const message = getErrorMessage(error).toLowerCase();

  // Common error codes
  if (code === 'INSUFFICIENT_FUNDS') return true;
  if (code === -32000) return true;

  // Message patterns
  if (message.includes('insufficient funds')) return true;
  if (message.includes('insufficient balance')) return true;
  if (message.includes('not enough balance')) return true;
  if (message.includes('exceeds the balance')) return true;

  return false;
}

/**
 * Insufficient NATIVE token (gas/rent), as opposed to an insufficient TRANSFER balance. Solana burns surface
 * "insufficient lamports N, need M" in the sim logs; EVM shows geth's "insufficient funds for gas". The
 * lamports text lives in the SendTransactionError `.logs`, so search those too (see getErrorLogs).
 */
export function isInsufficientNativeError(error: unknown): boolean {
  const text = `${getErrorMessage(error)} ${getErrorLogs(error)}`.toLowerCase();
  return (
    text.includes('insufficient lamports') ||                          // Solana native (lamports)
    text.includes('insufficient funds for gas') ||                     // EVM native gas (geth)
    text.includes('insufficient funds for intrinsic transaction cost') // EVM native gas (alt phrasing)
  );
}

/**
 * Native gas token symbol for a source chain — makes "not enough <TOKEN>" messages chain-correct.
 * Returns null for an unknown/unset chain so the caller can fall back to a generic phrasing.
 */
export function nativeTokenFor(sourceChain?: string): string | null {
  switch ((sourceChain || '').toLowerCase()) {
    case 'solana': return 'SOL';
    case 'polygon': return 'POL';
    case 'bnb': case 'bsc': case 'binance': return 'BNB';
    case 'tron': return 'TRX';
    case 'ethereum': case 'base': case 'optimism': case 'arbitrum': return 'ETH';
    default: return null;
  }
}

/**
 * Check if error is a network/connection error
 */
export function isNetworkError(error: unknown): boolean {
  const code = getErrorCode(error);
  const message = getErrorMessage(error).toLowerCase();

  // Common error codes
  if (code === 'NETWORK_ERROR') return true;

  // Message patterns
  if (message.includes('network error')) return true;
  if (message.includes('failed to fetch')) return true;
  if (message.includes('network request failed')) return true;
  if (message.includes('connection refused')) return true;
  if (message.includes('timeout')) return true;

  return false;
}

/**
 * Check if error is a chain switch error
 */
export function isChainSwitchError(error: unknown): boolean {
  const code = getErrorCode(error);
  const message = getErrorMessage(error).toLowerCase();

  // EIP-1193 unrecognized chain
  if (code === 4902) return true;

  // Message patterns
  if (message.includes('unrecognized chain')) return true;
  if (message.includes('switch network')) return true;
  if (message.includes('wrong chain')) return true;

  return false;
}

/**
 * JSON-RPC "Invalid Request" code. viem raises this as `InvalidRequestRpcError`
 * (node_modules/viem/_esm/errors/rpc.js:49-63) when the RPC/wallet REJECTS the JSON-RPC payload
 * viem sent — observed on Robinhood (4663) same-chain swaps (#261 F2). #301 verified LIVE that this
 * is PHANTOM'S OWN handling of chain 4663, not the network refusing the swap — so this is the one
 * code that keeps the wallet-attributed copy (see the classifier split below, #301 round 2).
 */
const INVALID_REQUEST_RPC_CODE = -32600;

/**
 * JSON-RPC "Method not found" code — viem's `MethodNotFoundRpcError`
 * (node_modules/viem/_esm/errors/rpc.js:64-78). UNLIKE -32600 above, this is a generic JSON-RPC
 * SERVER code ANY node can raise — including our own configured public RPC transports, not just a
 * quirky wallet. #301 round 2: blaming the wallet here was wrong for the same reason blaming
 * Robinhood used to be wrong — see the classifier split below for why it gets its own,
 * transport-neutral copy instead of joining the -32600 branch.
 */
const METHOD_NOT_FOUND_RPC_CODE = -32601;

/** Does THIS node of a cause chain carry the -32600 signature? Name OR code — see the walker below. */
function isInvalidRequestRpcNode(node: unknown): boolean {
  if (typeof node !== 'object' || node === null) return false;
  const n = node as { code?: unknown; name?: unknown };
  return n.code === INVALID_REQUEST_RPC_CODE || n.name === 'InvalidRequestRpcError';
}

/** Does THIS node of a cause chain carry the -32601 signature? Name OR code — see the walker below. */
function isMethodNotFoundRpcNode(node: unknown): boolean {
  if (typeof node !== 'object' || node === null) return false;
  const n = node as { code?: unknown; name?: unknown };
  return n.code === METHOD_NOT_FOUND_RPC_CODE || n.name === 'MethodNotFoundRpcError';
}

/**
 * Walk a possibly-wrapped error's cause chain looking for a node the given predicate matches.
 * Shared by both -32600 and -32601 detection below, so the "why" lives once.
 *
 * ── WHY A DEDICATED WALKER AND NOT `getErrorCode` ────────────────────────────────────────────────
 * `getErrorCode` inspects `.code`, `.error.code`, `.info.error.code`, `.status` and
 * `.response.status` — it NEVER inspects `.cause`. viem puts `code:-32600`/`-32601` on the inner
 * `RpcError` and wraps it in a `TransactionExecutionError` that has no `.code` at all, so
 * `getErrorCode` returns `undefined` for exactly these errors. Widening `getErrorCode` is out of
 * scope: it is shared by isUserRejection / isInsufficientFundsError / isChainSwitchError /
 * isNetworkError and would silently re-classify errors app-wide.
 *
 * ── WHY VIEM'S OWN WALKER ────────────────────────────────────────────────────────────────────────
 * `BaseError.walk(fn)` (viem/_esm/errors/base.js:78-91) already implements the cause traversal, so
 * we use it rather than rolling our own. The manual `.cause` loop below is the fallback for
 * non-viem shapes; it is DEPTH-CAPPED at 10 hops so a cyclic `.cause` cannot recurse forever.
 *
 * ── WHY NAME **OR** CODE ─────────────────────────────────────────────────────────────────────────
 * `rpc.js:19` sets the instance's `.code` from the CAUSE (`cause instanceof RpcRequestError ?
 * cause.code : code ?? -1`), not from the class constant, so a code-only match is fragile.
 */
function walksToRpcNode(error: unknown, predicate: (node: unknown) => boolean): boolean {
  if (typeof error !== 'object' || error === null) return false;

  // 1) viem's own cause walker, when this is a viem BaseError.
  //
  // NOT a bare `typeof walk === 'function'` + truthy-return check: `.walk` is duck-typed, and a
  // FOREIGN object that happens to expose an unrelated `.walk(fn)` method (different library,
  // different semantics — e.g. one that returns some other truthy value regardless of what `fn`
  // decided) would be accepted as a match on nothing but a coincidental method name. viem's real
  // `walk` returns the matched NODE itself when `fn` finds one (`base.js:78-86`), so re-validating
  // whatever it returned against the SAME predicate is a no-op for genuine viem errors and a hard
  // stop for anything else masquerading as one.
  const walk = (error as { walk?: (fn: (e: unknown) => boolean) => unknown }).walk;
  if (typeof walk === 'function') {
    try {
      const r = walk.call(error, predicate);
      if (Boolean(r) && predicate(r)) return true;
    } catch {
      // not viem's walk() — fall through to the manual chain below
    }
  }

  // 2) Plain-object `.cause` chain for non-viem shapes (and as a no-op safety net when viem's
  //    walker found nothing — it traverses the same chain with the same predicate).
  let node: unknown = error;
  for (let hops = 0; hops < 10 && node !== null && node !== undefined; hops++) {
    if (predicate(node)) return true;
    node = (node as { cause?: unknown }).cause;
  }
  return false;
}

/**
 * Detect a viem `InvalidRequestRpcError` (-32600) anywhere in the error's cause chain, or its raw
 * text form.
 *
 * ── WHY THIS ONE KEEPS THE WALLET-ATTRIBUTED COPY (#301 round 2) ────────────────────────────────
 * #301 verified LIVE that the Robinhood RPC handles every viem method including
 * `eth_sendRawTransaction`; the -32600 rejection is Phantom's OWN handling for chain 4663, not the
 * network refusing the send. So "your wallet doesn't support this" is the correct, wallet-attributed
 * fact for this one code.
 *
 * ── WHY A TEXT ARM ───────────────────────────────────────────────────────────────────────────────
 * The text arm uses `.includes()`, never `===`: the real shortMessage is
 * `'JSON is not a valid request object.'` (`rpc.js:52`), WITH a trailing period, so an equality
 * compare would be dead code that never fires.
 */
function isWalletUnsupportedRpcError(error: unknown): boolean {
  if (walksToRpcNode(error, isInvalidRequestRpcNode)) return true;
  return getErrorMessage(error).toLowerCase().includes('json is not a valid request object');
}

/**
 * Detect a viem `MethodNotFoundRpcError` (-32601) anywhere in the error's cause chain.
 *
 * ── WHY NO TEXT ARM (#301 round 2 fixer) ────────────────────────────────────────────────────────
 * The round-1 text arm matched `'does not exist / is not available'` as a bare `.includes()` against
 * ANY string — a substring match on free text that could let an unrelated error reach a
 * confidence-bearing classification branch. -32601 is only trusted here via its structural signature
 * (a real viem `MethodNotFoundRpcError` node, name or code, in the cause chain) — never by matching
 * free text that happens to contain that phrase.
 */
function isMethodNotFoundRpcError(error: unknown): boolean {
  return walksToRpcNode(error, isMethodNotFoundRpcNode);
}

/**
 * Categorize an error into an ErrorType for logging/handling
 *
 * @example
 * ```ts
 * catch (error) {
 *   const errorType = categorizeError(error, 'burn');
 *   console.log(`[${errorType}]`, getErrorMessage(error));
 * }
 * ```
 */
export function categorizeError(
  error: unknown,
  context?: 'burn' | 'attestation' | 'mint' | 'wallet' | 'api'
): ErrorType {
  // Check specific error types first
  if (isUserRejection(error)) return 'WalletRejectionError';
  if (isInsufficientFundsError(error)) return 'InsufficientFundsError';
  if (isChainSwitchError(error)) return 'ChainSwitchError';
  if (isNetworkError(error)) return 'NetworkError';

  // Context-based categorization
  if (context === 'burn') return 'BurnError';
  if (context === 'attestation') return 'AttestationError';
  if (context === 'mint') return 'MintError';
  if (context === 'wallet') return 'WalletConnectionError';
  if (context === 'api') return 'ApiError';

  return 'UnknownError';
}

/**
 * Get a user-friendly error message
 *
 * Transforms technical errors into messages that make sense to users.
 *
 * @example
 * ```ts
 * catch (error) {
 *   const friendlyMessage = getFriendlyErrorMessage(error);
 *   toast.error(friendlyMessage);
 * }
 * ```
 */
export function getFriendlyErrorMessage(error: unknown, sourceChain?: string): string {
  return classifyError(error, sourceChain).message;
}

/**
 * Short, stable support code for an error — e.g. 'CANCELLED', 'SLIPPAGE', 'WALLET_ERROR'.
 * Comes from the SAME classifier as getFriendlyErrorMessage, so the code and the message
 * can never drift. Use in console logs / telemetry, or alongside the message in the UI.
 */
export type ErrorTag =
  | 'CANCELLED'
  | 'INSUFFICIENT_FUNDS'
  | 'WRONG_NETWORK'
  | 'NETWORK'
  | 'SLIPPAGE'
  | 'QUOTE_EXPIRED'
  | 'PENDING_TX'
  | 'GAS'
  | 'ONCHAIN_REVERT'
  | 'WALLET_AUTH'
  | 'WALLET_ERROR'
  | 'RECEIPT_UNKNOWN'
  | 'UNKNOWN';

/**
 * Sentinel phrase that marks a receipt-wait timeout, whose status is UNKNOWN rather than failed.
 *
 * ── WHY A MARKER AND NOT A SUBSTRING GUESS ───────────────────────────────────────────────────────
 * `receiptTimeoutMessage` (utils/erc20Approve.ts) builds this message; this module owns the constant
 * so the producer and the classifier cannot drift into a silent mismatch that would send the message
 * back down the truncating UNKNOWN path. `errors.ts` has ZERO imports by design, so the dependency
 * runs one way only (erc20Approve → errors) and no cycle is possible.
 *
 * ── WHY THIS EXISTS AT ALL: THE TRUNCATION ATE THE WARNING ───────────────────────────────────────
 * The message is 237 chars. The UNKNOWN branch below caps at 100 + '…', which cut it to
 *   "Couldn't confirm the token approval within 180s. It may still be processing — check your wallet or a"
 * losing BOTH the transaction hash and the closing "so you don't submit it twice". Those two clauses
 * are the entire point of the message: the approve may well have mined, and a user who resubmits on
 * a mined allowance skips the no-op branch in `approveErc20Exact` and goes straight to the
 * value-moving call. The copy written specifically to prevent a double-submit was being truncated
 * away before it reached the person it was written for.
 */
export const RECEIPT_UNKNOWN_MARKER = 'It may still be processing';

/**
 * DEV-375 — is this display string one of the RECEIPT-UNKNOWN messages?
 *
 * ── WHY A STRING TEST AND NOT AN ERROR CLASS ─────────────────────────────────────────────────────
 * The marker is ALREADY the mechanism: `classifyError` at the `RECEIPT_UNKNOWN_MARKER` branch below
 * decides the tag from it, and `erc20Approve`'s two producers embed it. Introducing a second,
 * parallel mechanism (an `instanceof ReceiptUnknownError`) would give the app two answers that can
 * disagree — and the one that decides the TAG is this one. So the render layer asks the same
 * question the classifier asks, of the same text.
 *
 * The input is the DISPLAY string (`state.error`, `StoredDexOrder.error`, `burnError`), not a raw
 * error: those have already been through `getDisplayError`, and `RECEIPT_UNKNOWN` is one of the two
 * tags returned VERBATIM (no truncation, no ` · TAG` suffix), so the marker survives into them. A
 * message that a predicate ABOVE the marker check intercepts (`isNetworkError` &c.) is replaced by a
 * short canned string and correctly answers `false` here — which is exactly why I6(b) forbids the
 * UNKNOWN copy from containing any of those predicates' substrings.
 */
export function isReceiptUnknownMessage(message: string | null | undefined): boolean {
  return typeof message === 'string' && message.includes(RECEIPT_UNKNOWN_MARKER);
}

export function getErrorTag(error: unknown, sourceChain?: string): ErrorTag {
  return classifyError(error, sourceChain).tag;
}

/**
 * Combined user-facing string: the friendly "why" + a short support code, e.g.
 *   "Transaction was cancelled · CANCELLED"
 * NEVER contains the raw error/log blob. Use this at every error DISPLAY surface so
 * users get a clean reason plus a code they (and we) can reference.
 */
export function getDisplayError(error: unknown, sourceChain?: string): string {
  const { tag, message } = classifyError(error, sourceChain);
  // Append the code ONLY when it's meaningful. An UNKNOWN tag adds nothing to an already-clean
  // message and would just be noise (e.g. "Gas ceiling exceeded · UNKNOWN"), so show the bare
  // (already-bounded) message in that case. RECEIPT_UNKNOWN is bare for the same reason PLUS one of
  // its own: its message already ends in a hash the user is being asked to go and check, and a
  // trailing " · RECEIPT_UNKNOWN" reads as part of that hash.
  return tag === 'UNKNOWN' || tag === 'RECEIPT_UNKNOWN' ? message : `${message} · ${tag}`;
}

/**
 * The ONE classifier — single source of truth for both the message and the tag.
 * Order matters (most specific first); mirrors the historical getFriendlyErrorMessage branches.
 */
export function classifyError(error: unknown, sourceChain?: string): { tag: ErrorTag; message: string } {
  if (isUserRejection(error)) {
    return { tag: 'CANCELLED', message: 'Transaction was cancelled' };
  }

  // Insufficient NATIVE token for the network fee / account rent — NOT the transfer token. Checked BEFORE
  // the generic insufficient-funds/transfer-balance branch so native-gas isn't mislabeled a token-balance
  // issue, and named per source chain (SOL / ETH / POL / BNB / TRX). Solana surfaces "insufficient lamports"
  // in the sim logs (a "custom program error: 0x1"); EVM says "insufficient funds for gas".
  if (isInsufficientNativeError(error)) {
    const combined = `${getErrorMessage(error)} ${getErrorLogs(error)}`.toLowerCase();
    // "lamports" is definitionally Solana; otherwise use the source-chain hint (null → generic phrasing).
    const tok = combined.includes('lamports') ? 'SOL' : nativeTokenFor(sourceChain);
    return {
      tag: 'GAS',
      message: tok
        ? `You don’t have enough ${tok} to cover the network fee — add a little ${tok} to your wallet and try again.`
        : 'You don’t have enough native token to cover the network fee — add a little to your wallet and try again.',
    };
  }

  if (isInsufficientFundsError(error)) {
    return { tag: 'INSUFFICIENT_FUNDS', message: 'Insufficient balance for this transaction' };
  }

  if (isChainSwitchError(error)) {
    return { tag: 'WRONG_NETWORK', message: 'Please switch to the correct network' };
  }

  if (isNetworkError(error)) {
    return { tag: 'NETWORK', message: 'Network error - please check your connection and try again' };
  }

  const message = getErrorMessage(error);
  const logs = getErrorLogs(error); // Solana SendTransactionError .logs — where "insufficient lamports" lives
  const lower = (logs ? `${message} ${logs}` : message).toLowerCase();
  const code = getErrorCode(error);

  // Wallet hasn't authorized the account/method (EIP-1193 4100). The provider code is usually nested
  // under an ethers "could not coalesce" wrapper (so getErrorCode returns the top-level UNKNOWN_ERROR)
  // — match the message text too. Actionable fix for the user: reconnect / re-approve the account.
  if (code === 4100 || lower.includes('not been authorized') || lower.includes('not authorized by the user')) {
    return { tag: 'WALLET_AUTH', message: 'Your wallet hasn’t authorized this — reconnect your wallet and try again.' };
  }

  // RPC endpoint rejected the request (e.g. a domain-restricted key hitting an unlisted origin → 401 /
  // -32401, or a transient auth blip). Never surface the raw JSON-RPC blob — show a clean retry instead.
  if (code === 401 || code === -32401 || lower.includes('"code":-32401') || (lower.includes('unauthorized') && lower.includes('jsonrpc'))) {
    return { tag: 'NETWORK', message: 'Couldn’t reach the network right now — please try again in a moment.' };
  }

  // The wallet REJECTED viem's JSON-RPC payload (InvalidRequestRpcError, -32600). viem re-wraps it
  // in a TransactionExecutionError whose `.message` is a multi-line blob carrying a
  // `Request Arguments:` section (the user's `from` address, `to`, `value` and calldata) plus a
  // cosmetic `chain:  undefined (id: 4663)` line — none of which may ever reach a user. Reuses the
  // existing NETWORK tag (the union is not widened).
  //
  // #301 item #8 — THE COPY IS WALLET-ATTRIBUTED, AND NOT CHAIN-SPECIFIC. It previously named
  // Robinhood on `sourceChain === 'robinhood'`. #301 verified LIVE that the Robinhood RPC handles
  // every viem method including `eth_sendRawTransaction`: the -32600 is Phantom's OWN handling for
  // chain 4663, not the network refusing the swap. So the old copy blamed the wrong party — and
  // "this network isn't accepting the swap" was equally wrong for the same reason. Naming Robinhood
  // is now wrong on EVERY chain, Robinhood included, which is why the chain-specific branch is gone
  // rather than re-pointed.
  //
  // NOTE: this is a UX guarantee, NOT a fix for the underlying -32600; the transport root cause is
  // tracked separately (spec F2 / A1).
  if (isWalletUnsupportedRpcError(error)) {
    return { tag: 'NETWORK', message: "Your wallet doesn't currently support this transfer" };
  }

  // #301 round 2 (fixer) — -32601 (MethodNotFoundRpcError) was folded into the branch above at first,
  // but it is a DIFFERENT fact from -32600: a generic JSON-RPC server code any node can raise,
  // including our own configured public RPC transports, not a wallet quirk. It also fires in a
  // different, riskier moment — `waitForTransactionReceipt` runs over third-party public RPCs
  // AFTER the transaction is already broadcast, and only its own timeout is special-cased, so every
  // other throw here (including this one) reaches the user post-broadcast. Telling that user their
  // WALLET can't do it invites them to switch wallets and resubmit a transfer that may already be
  // landing — so this gets a transport-neutral message instead, consistent with the file's other
  // network-attributed copy (e.g. the -32401 branch above), never the wallet-blaming one.
  if (isMethodNotFoundRpcError(error)) {
    return { tag: 'NETWORK', message: "The network didn't respond to that request — please try again in a moment." };
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Common DEX / bridge / wallet revert classes.
  //
  // These match case-insensitively against the (possibly "message: revertReason")
  // string forwarded from the service layer (see services/api.ts), so that raw
  // contract/router revert text is replaced with actionable guidance. The
  // user-rejection / insufficient-funds / chain-switch / network branches above
  // take precedence; anything not matched here falls through to the truncated raw
  // message so the unknown long tail still says something useful.
  // ──────────────────────────────────────────────────────────────────────────

  // Token balance too low for the transfer (ERC-20 revert text).
  if (lower.includes('transfer amount exceeds balance') || lower.includes('exceeds balance')) {
    return { tag: 'INSUFFICIENT_FUNDS', message: 'Insufficient balance for this transaction' };
  }

  // Slippage / minimum-output protection tripped.
  if (
    lower.includes('slippage') ||
    lower.includes('min return') ||
    lower.includes('minimum amount') ||
    lower.includes('insufficient_output')
  ) {
    return { tag: 'SLIPPAGE', message: 'Price moved beyond your slippage tolerance — please retry.' };
  }

  // Quote / transaction deadline elapsed. Match context-specific tokens only:
  // a bare 'expired' also matched referrer/session/auth "expired" messages
  // (e.g. REFERRER_EXPIRED) → mislabeled as a stale swap quote. Anything no
  // longer matched here falls through to the raw (truncated) message, which for
  // those cases is already human-readable.
  if (
    lower.includes('quote expired') ||
    lower.includes('order expired') ||
    lower.includes('transaction too old') ||  // Uniswap V2/V3 deadline revert
    lower.includes('deadline')
  ) {
    return { tag: 'QUOTE_EXPIRED', message: 'This quote expired — please refresh and try again.' };
  }

  // A pending/stuck transaction is blocking this one (nonce / replacement).
  if (lower.includes('nonce too low') || lower.includes('replacement transaction underpriced')) {
    return { tag: 'PENDING_TX', message: 'A pending transaction is blocking this one — wait for it to clear and retry.' };
  }

  // Out of gas / gas estimate too high.
  if (lower.includes('gas required exceeds') || lower.includes('out of gas')) {
    return { tag: 'GAS', message: 'Not enough gas to complete this transaction.' };
  }

  // Generic on-chain failure with no decoded inner reason. Checked AFTER the
  // specific classes above so a decorated "execution reverted: slippage" still
  // maps to the slippage message.
  if (lower.includes('simulation failed') || lower.includes('execution reverted')) {
    return { tag: 'ONCHAIN_REVERT', message: 'The transaction would fail on-chain — please try again or adjust the amount.' };
  }

  // ethers v6 wraps provider errors it cannot map to a known code as:
  //   "could not coalesce error (error={…}, payload={…}, code=UNKNOWN_ERROR, version=6.x)"
  // Reaching here means it was not a rejection / insufficient-funds / revert / slippage we recognise,
  // so NEVER surface that raw blob to the user — show a clean, honest generic instead.
  if (lower.includes('could not coalesce') || lower.includes('unknown_error')) {
    return { tag: 'WALLET_ERROR', message: 'Your wallet couldn’t complete the transaction — please try again.' };
  }

  // Receipt-wait timeout — status UNKNOWN, NOT failed. Surfaced VERBATIM: this is the one message in
  // the app whose tail carries the safety-critical part (the tx hash and "so you don't submit it
  // twice"), so it is exempted from the 100-char cap below rather than shortened to fit it. Checked
  // BEFORE the UNKNOWN fallthrough, which is the branch that used to swallow it.
  if (message.includes(RECEIPT_UNKNOWN_MARKER)) {
    return { tag: 'RECEIPT_UNKNOWN', message };
  }

  // Unknown long tail: truncate (never blank), tagged UNKNOWN.
  //
  // ── WHY shortMessage + A BOUNDED DETAIL TAIL, AND NOT THE RAW `.message` ────────────────────────
  // A viem BaseError's `.message` is multi-line: shortMessage, then a `Request Arguments:` block
  // (which contains the user's `from` address, `to`, `value` and calldata), then `Details:` and
  // `Version:`. Truncating THAT to 100 chars leaks the head of the blob into the UI — the #261 F2
  // report. viem also exposes the one-line `.shortMessage` and the `.details` tail as fields, so
  // prefer those when present.
  //
  // NOT bare `.shortMessage`: `UnknownRpcError`'s is the content-free 'An unknown RPC error
  // occurred.' (rpc.js:440), while today's truncation surfaces ~59 useful chars of the `Details:`
  // tail — the documented intent of this branch. So keep a BOUNDED (60-char) detail suffix.
  //
  // Classification above is untouched — every branch still text-matches the FULL `.message`; only
  // this final "we don't know what this is" render changes. The RECEIPT_UNKNOWN check stays
  // strictly ABOVE this branch (it is exempt from truncation for a fund-safety reason).
  const viemFields = typeof error === 'object' && error !== null
    ? (error as { shortMessage?: unknown; details?: unknown })
    : null;
  const shortMessage = typeof viemFields?.shortMessage === 'string' && viemFields.shortMessage.length > 0
    ? viemFields.shortMessage
    : null;
  const details = viemFields?.details;
  // `details` must itself be a non-empty STRING, not merely truthy — a truthy check alone lets an
  // object/array `.details` through, which `String(details)` would render as the useless literal
  // '[object Object]' (or similar) instead of the intended human-readable tail.
  const hasDetails = typeof details === 'string' && details.length > 0;
  const display = shortMessage
    ? shortMessage + (hasDetails ? ' — ' + details.slice(0, 60) : '')
    : message;

  return {
    tag: 'UNKNOWN',
    message: display.length > 100 ? display.slice(0, 100) + '...' : display,
  };
}

/**
 * getDisplayError, but for the UNKNOWN tag returns the classified text BEFORE the 100-char on-screen
 * truncation is applied. Every other tag is already a fixed, address-free string (or, for
 * RECEIPT_UNKNOWN, the full untruncated message) with no internal truncation, so this is
 * byte-identical to getDisplayError for those.
 *
 * ── WHY THIS EXISTS (P1, #261 F2 GATE-4/S1 security finding) ────────────────────────────────────
 * The client-error POST (useDexBridge.ts) used to call getDisplayError() — already truncated to 100
 * chars in classifyError's UNKNOWN branch above — and redact address-shaped runs AFTER. An address
 * straddling that cut survived as a sub-40-char hex fragment the redaction regex could not see: e.g.
 * an address starting at char 60 is cut to `0x` + 38 hex, two characters short of the 40 the regex
 * requires, so nothing gets redacted and ~38/40 hex characters of a real address reached the DB. This
 * export lets the caller redact BEFORE any length bound instead of after.
 *
 * classifyError's own return value and the on-screen `errorMsg` string are completely UNCHANGED by
 * this addition — it is an additive, read-only derivation used only by the client-error report path.
 */
export function getReportDisplayError(error: unknown, sourceChain?: string): string {
  const { tag, message } = classifyError(error, sourceChain);
  if (tag !== 'UNKNOWN') {
    // Mirrors getDisplayError's own suffix rule exactly (see that function above).
    return tag === 'RECEIPT_UNKNOWN' ? message : `${message} · ${tag}`;
  }
  // Rebuild the UNKNOWN branch's pre-truncation display. This intentionally mirrors the exact
  // shortMessage/details construction inside classifyError's UNKNOWN branch above, MINUS the final
  // 100-char slice, so the two can never disagree on WHAT they classify — only on whether the length
  // bound is applied.
  const viemFields = typeof error === 'object' && error !== null
    ? (error as { shortMessage?: unknown; details?: unknown })
    : null;
  const shortMessage = typeof viemFields?.shortMessage === 'string' && viemFields.shortMessage.length > 0
    ? viemFields.shortMessage
    : null;
  const details = viemFields?.details;
  const hasDetails = typeof details === 'string' && details.length > 0;
  // GATE-4 re-pass F1. This fallback MUST be the pre-truncation text, not the destructured
  // `message` above — that one came back from classifyError and is ALREADY cut to 100 + '...'.
  // Returning it here defeated this whole function on every error with no `.shortMessage`, which
  // is precisely the shape the Tron corridor produces (a plain Error / TronWeb provider object),
  // and the shape backend API errors and deBridge revert strings take. An address straddling the
  // cut then reached the report as an un-redactable sub-40-hex fragment. `getErrorMessage(error)`
  // is exactly what classifyError's own UNKNOWN branch truncates, so the two cannot disagree on
  // WHAT they classify — only on whether the length bound is applied, which is this function's
  // entire reason to exist.
  return shortMessage
    ? shortMessage + (hasDetails ? ' — ' + details.slice(0, 60) : '')
    : getErrorMessage(error);
}

// ── Address redaction for the client-error POST body (GATE-4 finding G1) ──────────────────────────
// EXTRACTED from useDexBridge.ts's swap-catch block, where this was previously inline and therefore
// untestable in isolation. Pure, zero-import (this file has none by design), so the extraction is a
// straight move: useDexBridge.ts now calls this instead of re-deriving the same regex chain per-render.
//
// Mirrors the server's boundary-safe pattern (routes/client-error.ts EVM_ADDRESS_RE_G /
// BASE58_ADDRESS_RE_G / HEX_TXHASH_RUN_RE / ABI_PADDED_RE — see that file for the full derivation of
// each pattern): an optional `0[xX]` prefix bounded by lookbehind+lookahead (so a >=64-hex
// RECEIPT_UNKNOWN tx hash is never matched — every interior 40-char window of a run that long borders
// a hex digit on at least one side), PLUS a base58 pass for Tron/Solana addresses, PLUS a trailing-
// fragment backstop for any partial hex run a length bound elsewhere might still leave at the tail.
const REPORT_EVM_ADDRESS_RE_G = /(?<![0-9a-fA-F])(?:0[xX])?[0-9a-fA-F]{40}(?![0-9a-fA-F])/g;
const REPORT_BASE58_ADDRESS_RE_G = /[1-9A-HJ-NP-Za-km-z]{32,44}/g;
// `details.slice(0, 60)` can cut a BARE 40-hex address (no prefix) into a sub-40 fragment the EVM pass
// cannot match, and the slice leaves no ellipsis, so `$` really does anchor to it (GATE-4 re-pass F2).
const REPORT_TRAILING_HEX_FRAGMENT_RE = /(?:0[xX])?[0-9a-fA-F]{8,}$/;
// A >=64-hex run (RECEIPT_UNKNOWN's tx hash) is a candidate for the carve-out below — `a`-`f` are ALSO
// valid base58 letters, so a digit-light hex run (no `0`) can satisfy BASE58_ADDRESS_RE too, even
// though the EVM pass' lookbehind/lookahead already leaves a run this long alone (mirrors the
// identical guard in routes/client-error.ts HEX_TXHASH_RUN_RE).
const REPORT_HEX_RUN_RE = /([0-9a-fA-F]{64,})/;
// An ABI-encoded address is `000…000` + 40 hex chars — a 64-hex run, i.e. EXACTLY the shape the
// carve-out above protects, and exactly how an address appears in revert data. A genuine tx hash with
// >=20 leading zero nibbles is ~2^-80, so a long zero run is a reliable tell that this is padding
// around an address rather than a hash worth preserving (GATE-4 re-pass F4).
const REPORT_ABI_PADDED_RE = /^0{20,}/;

/**
 * Redact every address-shaped run out of the classified client-error report string
 * (`getReportDisplayError`'s output). For a >=64-hex captured run, the decision of "hash to preserve"
 * vs. "padding to redact" is made ONCE per run — see the STRUCTURAL FIX note below (G1).
 *
 * ── WHY THIS EXISTS (P1/P2, #261 F2 GATE-4/S1) ──────────────────────────────────────────────────
 * `errorMsg` (the on-screen string) is NOT always the classified string — the Tron branch returns raw
 * provider text verbatim on its fallthrough — so the POST deliberately re-derives its own message from
 * `getReportDisplayError` and redacts it here as a second, independent layer from the server's own
 * redact-not-reject pass (defence in depth, not the only control).
 *
 * ── STRUCTURAL FIX (GATE-4 finding G1) ──────────────────────────────────────────────────────────
 * A captured >=64-hex run used to be hex-run whether it was ABI padding OR the metadata is handed to
 * the base58 pass — but base58 excludes `0`, so any `0` nibble anywhere in the padded address broke
 * every >=32-char base58 run inside it and let the address survive (measured 81% leak rate on random
 * addresses, since ~93% of random 40-hex addresses contain at least one `0` nibble). The fix decides
 * ONCE, per captured run: if `REPORT_ABI_PADDED_RE` matches, it is padding around an address — redact
 * the WHOLE run, never hand it to a pattern that was never going to fully match it. If it does not
 * match, it is a hash — preserve it verbatim, exactly as before. This mirrors the identical structural
 * fix in `routes/client-error.ts` `redactAddresses` so the two layers can never disagree (P-D).
 */
export function redactAddressesForReport(message: string): string {
  return message
    .replace(REPORT_EVM_ADDRESS_RE_G, '0x…')
    .split(REPORT_HEX_RUN_RE)
    .map((segment, i) => {
      if (i % 2 === 1) {
        // Captured >=64-hex run: decide ONCE — ABI padding (redact the whole run) or a hash (keep it).
        // Previously this fell through to the base58+trailing-fragment pass below even when padded; that
        // happened to still fully redact on THIS layer only because the trailing-fragment regex's `$`
        // anchor coincides with the end of an isolated split segment — an accidental side effect of an
        // unrelated backstop, not a deliberate decision, and NOT true on the server (routes/
        // client-error.ts has no trailing-fragment regex at all, where the identical pattern leaked 81%
        // of random ABI-padded addresses). Deciding explicitly here, the same way on both layers, is what
        // makes "fixed symmetrically" a checkable claim (P-D) instead of a coincidence of layout.
        return REPORT_ABI_PADDED_RE.test(segment) ? '0x…' : segment;
      }
      return segment.replace(REPORT_BASE58_ADDRESS_RE_G, '…').replace(REPORT_TRAILING_HEX_FRAGMENT_RE, '0x…');
    })
    .join('');
}

/**
 * Walk the `.cause` chain (bounded to 10 hops — the same cap and cyclic-`.cause` safety rationale as
 * isInvalidRequestRpcError's fallback walker above) for the first node exposing a string or number
 * `.code`, falling back to getErrorCode(error) if none is found.
 *
 * ── WHY A SEPARATE EXPORT AND NOT A getErrorCode() CHANGE (P3, #261 F2 GATE-4/S1, NON-GOAL) ────────
 * `getErrorCode` deliberately does not walk `.cause` — it is shared by isUserRejection /
 * isInsufficientFundsError / isChainSwitchError / isNetworkError, and widening it would silently
 * re-classify errors app-wide. For the real Robinhood shape (TransactionExecutionError wrapping
 * InvalidRequestRpcError) the wrapper has no top-level `.code`; -32600 lives on `.cause`, so
 * `getErrorCode(e)` returns `undefined` for exactly the one error `client_error_logs.error_code`
 * exists to diagnose. This walker is additive and used only at the client-error POST call site.
 */
export function getErrorCodeDeep(error: unknown): string | number | undefined {
  let node: unknown = error;
  for (let hops = 0; hops < 10 && node !== null && node !== undefined; hops++) {
    if (typeof node === 'object' && node !== null) {
      const code = (node as { code?: unknown }).code;
      if (typeof code === 'string' || typeof code === 'number') return code;
    }
    node = (node as { cause?: unknown } | null)?.cause;
  }
  return getErrorCode(error);
}
